import React, { FC, ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Breakpoint,
} from '@mui/material';
import { X as CloseIcon } from 'react-feather';
import { PVLoadingButton } from './Buttons/PVLoadingButton';

/*
 * Types.
 */

interface PvDialogProps {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  title: string;
  children: ReactNode;
  confirmButtonText?: string;
  closeButtonText?: string;
  maxWidth?: false | Breakpoint;
  confirmDisabled?: boolean;
  onConfirm: (e: any) => void;
  tertiaryButtonText?: string;
  onTertiaryButtonClick?: () => void;
  tertiaryConfirmDisabled?: boolean;
  hideCloseButton?: boolean;
  showMinimalCloseButton?: boolean;
  hideConfirmButton?: boolean;
  secondaryConfirmButtonText?: string;
  secondaryConfirmDisabled?: boolean;
  onSecondaryConfirm?: () => void;
  dialogClassName?: string;
  hideActions?: boolean;
  confirmButtonColor?: 'inherit' | 'primary' | 'secondary';
  fullScreen?: boolean;
  withoutContent?: boolean;
  disableEnforceFocus?: boolean;
  showDividers?: boolean;
  noPad?: boolean;
  titleIcon?: ReactNode;
  tertiaryButtonVariant?: 'text' | 'outlined' | 'contained';
  tertiaryButtonIcon?: ReactNode;
}

export const PvDialog: FC<PvDialogProps> = ({
  open,
  onClose,
  loading = false,
  title,
  children,
  confirmButtonText = 'Confirm',
  closeButtonText = 'Close',
  maxWidth = 'xs',
  confirmDisabled = false,
  onConfirm,
  tertiaryButtonText = undefined,
  onTertiaryButtonClick = undefined,
  tertiaryConfirmDisabled = undefined,
  hideCloseButton = false,
  showMinimalCloseButton = false,
  hideConfirmButton = false,
  secondaryConfirmButtonText = undefined,
  secondaryConfirmDisabled = undefined,
  onSecondaryConfirm = undefined,
  dialogClassName = undefined,
  hideActions = false,
  confirmButtonColor = 'secondary',
  fullScreen = false,
  withoutContent = false,
  disableEnforceFocus = false,
  showDividers = false,
  noPad = false,
  titleIcon = undefined,
  tertiaryButtonVariant = undefined,
  tertiaryButtonIcon = undefined,
}: PvDialogProps) => (
  <Dialog
    fullScreen={fullScreen}
    className={dialogClassName}
    open={!!open}
    onClose={onClose}
    fullWidth
    maxWidth={maxWidth}
    disableEnforceFocus={disableEnforceFocus}
    data-cy="pv-dialog-modal"
  >
    {title && (
      <div>
        <DialogTitle
          id="form-dialog-title"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          {titleIcon && titleIcon}
          {title}
        </DialogTitle>
      </div>
    )}
    {showMinimalCloseButton && (
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
    )}
    {showDividers && <Divider sx={{ bgcolor: 'rgba(0, 0, 0, 0.1)' }} />}
    {!withoutContent && (
      <DialogContent
        style={{
          paddingLeft: noPad ? 0 : '24px',
          paddingRight: noPad ? 0 : '24px',
          paddingTop: '6px',
          paddingBottom: '0px',
        }}
      >
        {children}
      </DialogContent>
    )}
    {withoutContent && <div>{children}</div>}
    {!hideActions && (
      <DialogActions
        sx={{ marginBottom: fullScreen ? '16px' : 0 }}
      >
        <div style={{ flexGrow: 1 }}>
          {tertiaryButtonText && onTertiaryButtonClick && (
            <Button
              disabled={tertiaryConfirmDisabled || loading}
              onClick={onTertiaryButtonClick}
              variant={tertiaryButtonVariant}
              startIcon={tertiaryButtonIcon}
              style={{ float: 'left' }}
            >
              {tertiaryButtonText}
            </Button>
          )}
        </div>
        {!hideCloseButton && (
          <Button
            disabled={loading}
            onClick={onClose}
            variant="outlined"
            color="secondary"
          >
            {closeButtonText}
          </Button>
        )}
        {secondaryConfirmButtonText && (
          <PVLoadingButton
            forceLoading={loading}
            disabled={secondaryConfirmDisabled || loading}
            onClick={onSecondaryConfirm}
          >
            {secondaryConfirmButtonText}
          </PVLoadingButton>
        )}
        {!hideConfirmButton && (
          <PVLoadingButton
            forceLoading={loading}
            disabled={confirmDisabled || loading}
            onClick={onConfirm}
            color={confirmButtonColor}
            testId="modal-submit-button"
          >
            {confirmButtonText}
          </PVLoadingButton>
        )}
      </DialogActions>
    )}
  </Dialog>
);

export default PvDialog;
